import { useState } from "react";
import type { SetterData } from "@/hooks/useAttributionData";

function showRateColor(rate: number): string {
  if (rate >= 70) return "text-emerald-400";
  if (rate >= 50) return "text-yellow-400";
  return "text-red-400";
}

function StageBadge({ stage }: { stage: string }) {
  const styles: Record<string, string> = { closed: "bg-emerald-500/20 text-emerald-400", demo_booked: "bg-yellow-500/20 text-yellow-400", no_show: "bg-red-500/20 text-red-400", lead: "bg-muted text-muted-foreground" };
  const labels: Record<string, string> = { closed: "closed", demo_booked: "demo booked", no_show: "no-show", lead: "lead" };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${styles[stage] ?? styles.lead}`}>{labels[stage] ?? stage}</span>;
}

function initials(name: string): string { return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase(); }

function SetterExpandedContent({ setter }: { setter: SetterData }) {
  return (
    <div className="border-t border-border p-4" onClick={(e) => e.stopPropagation()}>
      <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase mb-2">Recent Bookings</p>
      <table className="w-full text-xs">
        <thead><tr className="text-muted-foreground border-b border-border">
          <th className="text-left py-1 font-medium">Contact</th>
          <th className="text-left py-1 font-medium">Campaign</th>
          <th className="text-left py-1 font-medium">Stage</th>
          <th className="text-right py-1 font-medium">Deal</th>
        </tr></thead>
        <tbody>{setter.recentBookings.map((lead) => (
          <tr key={lead.id} className="border-b border-border/50 text-muted-foreground">
            <td className="py-1">{lead.contact_name || lead.ghl_contact_id}</td>
            <td className="py-1 text-blue-400 text-[10px]">{lead.campaign_name ?? "—"}</td>
            <td className="py-1"><StageBadge stage={lead.stage} /></td>
            <td className="text-right py-1">{lead.deal_value_cents ? <span className="text-emerald-400">${(lead.deal_value_cents / 100).toLocaleString()}</span> : "—"}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

interface SettersTabProps { setters: SetterData[]; loading: boolean; }

export function SettersTab({ setters, loading }: SettersTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const COLS = 3;

  if (loading) return <div className="grid grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="rounded-lg border border-border bg-card p-4 h-40 animate-pulse" />)}</div>;
  if (setters.length === 0) return <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No setter data for this period.</div>;

  const rows: SetterData[][] = [];
  for (let i = 0; i < setters.length; i += COLS) rows.push(setters.slice(i, i + COLS));
  const expandedSetter = setters.find(s => s.setterId === expandedId) ?? null;
  const expandedRowIdx = expandedId ? Math.floor(setters.findIndex(s => s.setterId === expandedId) / COLS) : -1;

  return (
    <div className="space-y-0">
      {rows.map((row, rowIdx) => (
        <div key={rowIdx}>
          <div className="grid grid-cols-3 gap-4 mb-0">
            {row.map((setter) => {
              const isExpanded = expandedId === setter.setterId;
              return (
                <div key={setter.setterId} className={`rounded-lg border bg-card p-4 cursor-pointer transition-all duration-200 ${isExpanded ? "border-primary/40" : "border-border hover:border-primary/20"}`} onClick={() => setExpandedId(isExpanded ? null : setter.setterId)}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-violet-400 flex items-center justify-center text-xs font-bold text-background shrink-0">{initials(setter.setterName)}</div>
                    <div className="min-w-0"><p className="text-sm font-semibold text-foreground truncate">{setter.setterName}</p><p className="text-[10px] text-muted-foreground">Setter</p></div>
                    <span className="ml-auto text-muted-foreground text-xs">{isExpanded ? "↑" : "↓"}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Demos Booked</p><p className="text-lg font-bold text-cyan-400">{setter.demosBooked}</p></div>
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Show Rate</p><p className={`text-lg font-bold ${showRateColor(setter.showRate)}`}>{setter.showRate}%</p></div>
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Closed</p><p className="text-lg font-bold text-violet-400">{setter.demosClosed}</p></div>
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Close Rate</p><p className="text-lg font-bold text-foreground">{setter.closeRate}%</p></div>
                  </div>
                  <div className="h-1 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: `${Math.min(setter.showRate, 100)}%` }} /></div>
                  <p className="text-[10px] text-muted-foreground mt-1">{setter.showRate}% show rate</p>
                </div>
              );
            })}
          </div>
          {expandedRowIdx === rowIdx && expandedSetter && (
            <div className="rounded-lg border border-primary/30 bg-card/80 mb-4">
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <span className="text-sm font-bold text-foreground">{expandedSetter.setterName} — Recent Bookings</span>
                <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setExpandedId(null)}>↑ collapse</button>
              </div>
              <SetterExpandedContent setter={expandedSetter} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
